import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Form, Input, MessagePlugin, Select, Space, Textarea } from 'tdesign-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMemoizedFn } from 'ahooks';
import { connect } from 'react-redux';
import { createScript, getScriptById, updateScript } from '@/services/templateService';
import Tip from '@/components/Tip';
import Panel from '@/components/Panel';
import { clusterTypeOptions } from '@/utils/constant';
import { isVoid, sleep, toTsOptions } from '@/utils/common';
import { fetchVariableStencilList } from '@/slices/variableSlice';
import Code from './Code';
import Variable from './Variable';

const { FormItem } = Form;

function Detail({ fetchVariableStencilList }) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { id } = useParams();
  const [submitting, setSubmitting] = useState(false);

  const original = Form.useWatch('original', form);

  const isEdit = !isVoid(id);

  const loadDetail = useMemoizedFn(async () => {
    const { data } = await getScriptById(id);
    const variableList = data.scriptStencilVariableDetailVOList?.map((item) => ({
      id: item.id,
      varId: item.varId,
      variableLevel: item.variableLevel,
      key: item.key,
      label: item.label,
      desc: item.desc,
    }));
    form.setFieldsValue({
      scriptName: data.scriptName,
      original: data.scriptOriginal,
      scriptNote: data.scriptNote,
      type: data.scriptType,
      scriptContent: data.scriptContent,
      variableListCluster: variableList?.filter((item) => item.variableLevel === 0),
    });
    // td bug 字表需重新设置 后期调整为components/Form
    await sleep(100);
    form.setFieldsValue({
      variableListIp: variableList?.filter((item) => item.variableLevel === 1),
    });
  });

  useEffect(() => {
    if (isEdit) {
      loadDetail();
    }
  }, [isEdit, loadDetail]);

  const changeOrigin = (value) => {
    if (!isVoid(value)) {
      form.reset({ fields: ['variableListCluster', 'variableListIp'] });
      fetchVariableStencilList({ scopeType: value });
    }
  };

  const handleSubmit = async ({ validateResult, fields }) => {
    const values = {
      ...fields,
      scriptVariableBOList: []
        .concat(fields.variableListCluster || [])
        .concat(fields.variableListIp || []),
    };
    if (validateResult === true) {
      setSubmitting(true);

      try {
        if (isEdit) {
          await updateScript({ ...values, scriptStencilId: id });
        } else {
          await createScript(values);
        }
        MessagePlugin.success('操作成功！');
        navigate('/maintenance/template/script');
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <Root>
      <Form form={form} labelWidth={140} onSubmit={handleSubmit} initialData={{ type: 0 }}>
        <Body>
          <Container>
            <Left className='narrow-scrollbar'>
              <Tip
                size='big'
                content={
                  isEdit
                    ? '查看与修改pulsar脚本'
                    : '新建脚本：定义一个新的脚本，可以添加已有组件的变量信息以及为该脚本定义变量在执行阶段使用'
                }
                style={{ marginBottom: 24 }}
              >
                {isEdit ? '修改脚本' : '新建脚本'}
              </Tip>
              <FormContainer>
                <FormItem name='scriptName' label='脚本名称' rules={[{ required: true }]}>
                  <Input clearable />
                </FormItem>
                <FormItem name='original' label='脚本来源' rules={[{ required: true }]}>
                  <Select
                    onChange={changeOrigin}
                    options={toTsOptions(clusterTypeOptions)}
                    clearable
                  />
                </FormItem>
                <FormItem name='scriptNote' label='脚本作用说明' rules={[{ required: true }]}>
                  <Textarea rows={4} placeholder='请输入' clearable />
                </FormItem>
                <FormItem name='variableListCluster' label='集群变量'>
                  <Variable form={form} original={original} variableLevel={0} />
                </FormItem>
                {original === 0 && (
                  <FormItem name='variableListIp' label='IP级变量'>
                    <Variable form={form} original={original} variableLevel={1} />
                  </FormItem>
                )}
              </FormContainer>
            </Left>
            <Right>
              <Code />
            </Right>
          </Container>
          <Footer>
            <Space size='small'>
              <Button
                theme='default'
                variant='outline'
                onClick={() => navigate('/maintenance/template/script')}
              >
                取消
              </Button>
              <Button theme='primary' loading={submitting} type='submit'>
                提交
              </Button>
            </Space>
          </Footer>
        </Body>
      </Form>
    </Root>
  );
}

export default connect(null, {
  fetchVariableStencilList,
})(Detail);

const Root = styled.div`
  padding-right: 20px;
`;

const Body = styled(Panel)`
  display: flex;
  flex-direction: column;
  .shadow-card-body {
    padding: 0;
  }
`;

const Container = styled.div`
  flex: 1;
  display: flex;
`;

const Left = styled.div`
  /* width: 512px; */
  height: 100%;
  overflow-y: auto;
  flex: 4;
  width: 0;
  border-right: 1px solid #e7e7e7;
  padding: 24px;
  height: calc(100vh - 250px);
`;

const FormContainer = styled.div`
  width: 90%;
  height: 0;
`;

const Right = styled.div`
  flex: 5;
  width: 0;
`;

const Footer = styled.div`
  border-top: 1px solid #e7e7e7;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 24px;
`;
